import { useCallback, useEffect, useRef, useState } from 'react';
import { Content, Header, Page, Progress, MarkdownContent } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import './Olympus.css';

const HERMES_PROJECT_ID = 55373;
const SOLUTIONS = ['management', 'tech-community'] as const;
type Solution = (typeof SOLUTIONS)[number];

type ComponentStatus = 'done' | 'fail' | 'running' | 'wait';
type ChangeStatus = 'running' | 'done' | 'fail' | 'plan';

interface ChangeComponent {
  name: string;
  status: ComponentStatus;
  detail: string;
  mrUrl: string | null;
  mrMerged: boolean;
}

interface ChangeStage {
  label: string;
  status: 'done' | 'run' | 'wait' | 'fail' | 'plan';
}

interface Change {
  id: string;
  requirement: string;
  status: ChangeStatus;
  badge: string;
  age: string;
  stages: ChangeStage[];
  components: ChangeComponent[];
}

interface ManifestComponent {
  name: string;
  status: 'completed' | 'failed' | 'implementing' | 'pending' | string;
  branch?: string | null;
  mr_url?: string | null;
}

interface ManifestChange {
  name: string;
  requirement: string;
  status: 'implementing' | 'completed' | 'failed' | 'planned' | 'planning' | string;
  created_at: string;
  artifacts?: { proposal?: boolean; spec?: boolean; design?: boolean; tasks?: boolean };
  components?: ManifestComponent[];
}

interface Manifest {
  solution: string;
  updated_at: string;
  changes: ManifestChange[];
}

type ArtifactFile = 'proposal' | 'spec' | 'design' | 'tasks';

type ArtifactState = {
  status: 'loading' | 'loaded' | 'missing' | 'error';
  content?: string;
};

const STAGE_FILE: Partial<Record<string, ArtifactFile>> = {
  Proposal: 'proposal',
  Spec: 'spec',
  Design: 'design',
  Tasks: 'tasks',
};

function splitSections(md: string): Record<string, string> {
  const sections: Record<string, string> = {};
  let current: string | null = null;
  let buf: string[] = [];
  for (const line of md.split('\n')) {
    const m = /^##\s+(.+?)\s*$/.exec(line);
    if (m) {
      if (current) sections[current] = buf.join('\n').trim();
      current = m[1];
      buf = [];
    } else if (current) {
      buf.push(line);
    }
  }
  if (current) sections[current] = buf.join('\n').trim();
  return sections;
}

function relativeTime(iso: string): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'ahora mismo';
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h}h`;
  const d = Math.floor(h / 24);
  return `hace ${d} día${d !== 1 ? 's' : ''}`;
}

const COMPONENT_STATUS_MAP: Record<string, ComponentStatus> = {
  completed: 'done',
  failed: 'fail',
  implementing: 'running',
  pending: 'wait',
};

const COMPONENT_DETAIL_MAP: Record<string, string> = {
  pending: 'pendiente',
  implementing: 'implementando',
};

const CHANGE_STATUS_MAP: Record<string, ChangeStatus> = {
  implementing: 'running',
  completed: 'done',
  failed: 'fail',
  planned: 'plan',
  planning: 'plan',
};

const CHANGE_BADGE_MAP: Record<string, string> = {
  implementing: 'implementando',
  completed: 'completado',
  failed: 'fallido',
  planned: 'planificado',
  planning: 'planificando',
};

function manifestToChanges(manifest: Manifest | null): Change[] {
  if (!manifest || !manifest.changes) return [];
  return [...manifest.changes].reverse().map(c => {
    const arts = c.artifacts || {};
    const components: ChangeComponent[] = (c.components || []).map(comp => ({
      name: comp.name,
      status: COMPONENT_STATUS_MAP[comp.status] || 'wait',
      detail: comp.branch || COMPONENT_DETAIL_MAP[comp.status] || comp.status,
      mrUrl: comp.mr_url || null,
      mrMerged: c.status === 'completed',
    }));

    const execStage: ChangeStage['status'] =
      c.status === 'implementing' ? 'run' : c.status === 'completed' ? 'done' : c.status === 'failed' ? 'fail' : 'wait';

    const stages: ChangeStage[] = [
      { label: 'Proposal', status: arts.proposal ? 'done' : 'wait' },
      { label: 'Spec', status: arts.spec ? 'done' : 'wait' },
      { label: 'Design', status: arts.design ? 'done' : 'wait' },
      { label: 'Tasks', status: arts.tasks ? 'done' : 'wait' },
      { label: 'Ejecución', status: execStage },
    ];

    return {
      id: c.name,
      requirement: c.requirement,
      status: CHANGE_STATUS_MAP[c.status] || 'plan',
      badge: CHANGE_BADGE_MAP[c.status] || c.status,
      age: relativeTime(c.created_at),
      stages,
      components,
    };
  });
}

function stageIcon(s: ChangeStage['status']): string {
  return { done: '✓', run: '●', wait: '○', fail: '✕', plan: '·' }[s] || '○';
}

const COMPONENT_STATUS_LABEL: Record<ComponentStatus, string> = {
  running: 'implementando',
  done: 'completado',
  fail: 'fallido',
  wait: 'pendiente',
};

export const OlympusPage = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [currentSolution, setCurrentSolution] = useState<Solution>('management');
  const [solutionsData, setSolutionsData] = useState<Record<Solution, Change[] | null>>({
    management: null,
    'tech-community': null,
  });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [artifacts, setArtifacts] = useState<Record<string, ArtifactState>>({});
  const [openPanels, setOpenPanels] = useState<Set<string>>(new Set());
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchManifest = useCallback(
    async (solution: Solution): Promise<Manifest | null> => {
      const proxyBase = await discoveryApi.getBaseUrl('proxy');
      const path = `state/${solution}.json`;
      const url = `${proxyBase}/gitlabci/projects/${HERMES_PROJECT_ID}/repository/files/${encodeURIComponent(path)}/raw?ref=main`;
      try {
        const res = await fetchApi.fetch(url);
        if (res.status === 404) return { solution, updated_at: '', changes: [] };
        if (!res.ok) {
          // eslint-disable-next-line no-console
          console.warn('Hermes API', res.status);
          return null;
        }
        return await res.json();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('fetchManifest:', e);
        return null;
      }
    },
    [discoveryApi, fetchApi],
  );

  const fetchArtifact = useCallback(
    async (changeId: string, file: ArtifactFile): Promise<string | null> => {
      const proxyBase = await discoveryApi.getBaseUrl('proxy');
      const path = `openspec/changes/${changeId}/${file}.md`;
      const url = `${proxyBase}/gitlabci/projects/${HERMES_PROJECT_ID}/repository/files/${encodeURIComponent(path)}/raw?ref=main`;
      try {
        const res = await fetchApi.fetch(url);
        if (res.status === 404) return null;
        if (!res.ok) {
          // eslint-disable-next-line no-console
          console.warn('Hermes API', res.status);
          return null;
        }
        return await res.text();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('fetchArtifact:', e);
        return null;
      }
    },
    [discoveryApi, fetchApi],
  );

  const refresh = useCallback(
    async (solution: Solution, opts?: { invalidateArtifacts?: boolean }) => {
      setLoading(true);
      if (opts?.invalidateArtifacts) setArtifacts({});
      const manifest = await fetchManifest(solution);
      if (manifest !== null) {
        setSolutionsData(prev => ({ ...prev, [solution]: manifestToChanges(manifest) }));
      }
      setLoading(false);
    },
    [fetchManifest],
  );

  useEffect(() => {
    if (solutionsData[currentSolution] === null) {
      refresh(currentSolution);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSolution]);

  useEffect(() => {
    if (pollRef.current) clearTimeout(pollRef.current);
    const items = solutionsData[currentSolution];
    if (items && items.some(c => c.status === 'running')) {
      pollRef.current = setTimeout(() => refresh(currentSolution), 30000);
    }
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [solutionsData, currentSolution, refresh]);

  useEffect(() => {
    const items = solutionsData[currentSolution];
    if (!items) return;
    items.forEach(c => {
      const key = `${c.id}::proposal`;
      if (c.stages[0]?.status !== 'done' || artifacts[key]) return;
      setArtifacts(prev => ({ ...prev, [key]: { status: 'loading' } }));
      fetchArtifact(c.id, 'proposal').then(content => {
        setArtifacts(prev => ({
          ...prev,
          [key]: content === null ? { status: 'missing' } : { status: 'loaded', content },
        }));
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solutionsData, currentSolution]);

  const toggleExpanded = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const items = solutionsData[currentSolution];

  return (
    <Page themeId="tool">
      <Header title="Olympus" subtitle="Seguimiento de cambios autónomos (Zeus)" />
      <Content>
        <div className="olympus-page">
          <div className="olympus-tabs">
            {SOLUTIONS.map(sol => (
              <button
                key={sol}
                type="button"
                className={`olympus-tab${sol === currentSolution ? ' active' : ''}`}
                onClick={() => setCurrentSolution(sol)}
              >
                {sol}
              </button>
            ))}
            <div className="olympus-toolbar">
              <button
                type="button"
                className="olympus-btn-icon"
                title="Actualizar"
                onClick={() => refresh(currentSolution, { invalidateArtifacts: true })}
                disabled={loading}
              >
                {loading ? '⟳' : '↺'}
              </button>
              <button
                type="button"
                className="olympus-btn-trigger"
                onClick={() => setTriggerOpen(v => !v)}
              >
                + New Change
              </button>
            </div>
          </div>

          <div className="olympus-main">
            <div className={`olympus-trigger-panel${triggerOpen ? ' open' : ''}`}>
              <div className="olympus-trigger-header" onClick={() => setTriggerOpen(v => !v)}>
                <h3>
                  <span className="dot" /> Disparar Zeus
                </h3>
                <span className="olympus-chevron">▾</span>
              </div>
              {triggerOpen && (
                <div className="olympus-trigger-body">
                  <textarea
                    placeholder='Describe el requerimiento en lenguaje natural… ej: "Agregar campo departamento al perfil del empleado"'
                    rows={3}
                  />
                  <div className="olympus-trigger-row">
                    <select className="olympus-sol-select" defaultValue={currentSolution}>
                      {SOLUTIONS.map(sol => (
                        <option key={sol} value={sol}>
                          {sol}
                        </option>
                      ))}
                    </select>
                    <span className="olympus-trigger-hint">
                      Disparo de pipeline pendiente por confirmar con equipo Zeus
                    </span>
                    <button className="olympus-btn-trigger" style={{ marginLeft: 'auto' }} disabled>
                      Disparar
                    </button>
                  </div>
                </div>
              )}
            </div>

            <p className="olympus-section-label">
              {currentSolution} · {items === null ? 'cargando…' : `${items.length} change${items.length !== 1 ? 's' : ''}`}
            </p>

            {loading && items === null && <Progress />}

            {items !== null && items.length === 0 && (
              <div className="olympus-empty">
                No hay changes para {currentSolution}.
                <br />
                <br />
                <span style={{ opacity: 0.6, fontSize: 12 }}>
                  Dispara el primer pipeline con &quot;New Change&quot;
                </span>
              </div>
            )}

            {items?.map(change => {
              const isExpanded = expanded.has(change.id);
              const proposalArt = artifacts[`${change.id}::proposal`];
              const sections = proposalArt?.content ? splitSections(proposalArt.content) : null;
              const why = sections?.Why;
              const impact = sections?.Impact;

              const handlePillClick = (stage: ChangeStage) => {
                if (stage.label === 'Ejecución') {
                  toggleExpanded(change.id);
                  return;
                }
                const file = STAGE_FILE[stage.label];
                if (!file) return;
                const key = `${change.id}::${file}`;
                setOpenPanels(prev => {
                  const next = new Set(prev);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  return next;
                });
                if (artifacts[key]) return;
                if (stage.status !== 'done') {
                  setArtifacts(prev => ({ ...prev, [key]: { status: 'missing' } }));
                  return;
                }
                setArtifacts(prev => ({ ...prev, [key]: { status: 'loading' } }));
                fetchArtifact(change.id, file).then(content => {
                  setArtifacts(prev => ({
                    ...prev,
                    [key]: content === null ? { status: 'missing' } : { status: 'loaded', content },
                  }));
                });
              };

              return (
                <div className="olympus-card" key={change.id}>
                  <div className="olympus-card-head" onClick={() => toggleExpanded(change.id)}>
                    <div className="olympus-card-top">
                      <span className={`olympus-status-dot ${change.status}`} />
                      <span className="olympus-card-name">{change.id}</span>
                      <span className={`olympus-badge ${change.status}`}>{change.badge}</span>
                    </div>
                    <p className="olympus-card-req">{change.requirement}</p>
                  </div>

                  {(why || impact || proposalArt?.status === 'loading') && (
                    <div className="olympus-impact">
                      {proposalArt?.status === 'loading' && !why && !impact && (
                        <span className="olympus-impact-loading">cargando análisis de impacto…</span>
                      )}
                      {why && (
                        <div className="olympus-impact-block">
                          <h4 className="olympus-impact-label">Por qué</h4>
                          <MarkdownContent content={why} />
                        </div>
                      )}
                      {impact && (
                        <div className="olympus-impact-block">
                          <h4 className="olympus-impact-label">Impacto</h4>
                          <MarkdownContent content={impact} />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="olympus-pipeline">
                    {change.stages.map((stage, i) => (
                      <div key={stage.label} style={{ display: 'flex', alignItems: 'center' }}>
                        <div
                          className={`olympus-stage-pill ${stage.status} clickable`}
                          onClick={() => handlePillClick(stage)}
                        >
                          <span>{stageIcon(stage.status)}</span> {stage.label}
                        </div>
                        {i < change.stages.length - 1 && <div className="olympus-conn" />}
                      </div>
                    ))}
                  </div>

                  {(['proposal', 'spec', 'design', 'tasks'] as const).map(file => {
                    const key = `${change.id}::${file}`;
                    if (!openPanels.has(key)) return null;
                    const art = artifacts[key];
                    return (
                      <div className="olympus-artifact-panel" key={key}>
                        {(!art || art.status === 'loading') && (
                          <span className="olympus-impact-loading">cargando {file}.md…</span>
                        )}
                        {art?.status === 'missing' && (
                          <span className="olympus-artifact-empty">Aún no generado.</span>
                        )}
                        {art?.status === 'error' && (
                          <span className="olympus-artifact-empty">No se pudo cargar {file}.md.</span>
                        )}
                        {art?.status === 'loaded' && art.content && <MarkdownContent content={art.content} />}
                      </div>
                    );
                  })}

                  {isExpanded && (
                    <div className="olympus-comps">
                      {change.components.map((comp, i) => (
                        <div className="olympus-comp-row" key={`${comp.name}-${i}`}>
                          <span className="olympus-comp-name">{comp.name || '—'}</span>
                          <span className={`olympus-comp-status ${comp.status}`}>
                            {COMPONENT_STATUS_LABEL[comp.status]}
                          </span>
                          <span className="olympus-comp-detail">{comp.detail}</span>
                          {comp.mrUrl && (
                            <a
                              className="olympus-mr-link"
                              href={comp.mrUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              MR →{comp.mrMerged ? ' ✓' : ''}
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="olympus-card-foot">
                    <span className="olympus-foot-left">
                      {[change.age, `${change.components.length} componente${change.components.length !== 1 ? 's' : ''}`]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    <button className="olympus-foot-toggle" onClick={() => toggleExpanded(change.id)}>
                      {isExpanded ? 'Ocultar' : 'Componentes'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Content>
    </Page>
  );
};
